import React, { useState } from 'react';
import { Modal, Button } from '../ui';
import { X, Split } from 'lucide-react';
import { safeArray } from '../../utils/safe';
import type { Order } from '../../types/@types/order';

interface SplitOrderModalProps {
  open: boolean;
  order: Order | null;
  onClose: () => void;
  onConfirm: (itemIds: (string | number)[]) => void;
  loading?: boolean;
}

export const SplitOrderModal: React.FC<SplitOrderModalProps> = ({
  open,
  order,
  onClose,
  onConfirm,
  loading,
}) => {
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  const toggleItem = (id: string | number) => {
    setSelectedIds((prev) => {
      const safePrev = safeArray(prev);
      return safePrev.includes(id) ? safePrev.filter((i) => i !== id) : [...safePrev, id];
    });
  };

  const handleConfirm = () => {
    if (selectedIds.length === 0) return;
    onConfirm(selectedIds);
  };

  if (!order) return null;

  // Only show items that are not cancelled
  const items = safeArray(order.items).filter(i => i.status !== 'CANCELLED');

  return (
    <Modal open={open} onClose={onClose} titleId="split-modal-title" className="max-w-2xl">
      <div className="flex items-start justify-between border-b border-border px-6 py-4">
        <div>
          <h2 id="split-modal-title" className="text-lg font-semibold text-text">
            Dividir Cuenta
          </h2>
          <p className="text-sm text-text-muted">Selecciona los productos para mover a una nueva cuenta</p>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="h-9 w-9 rounded-full">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.id}
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer ${
                selectedIds.includes(item.id)
                  ? 'bg-blue-50 border-blue-500'
                  : 'bg-white border-border hover:bg-gray-50'
              }`}
              onClick={() => toggleItem(item.id)}
            >
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(item.id)}
                  onChange={() => toggleItem(item.id)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <p className="font-medium text-text">{item.product?.name || 'Producto'}</p>
                  <p className="text-xs text-text-muted">Cantidad: {item.quantity}</p>
                </div>
              </div>
              <span className="font-semibold text-text">
                ${(item.line_subtotal || 0).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-border px-6 py-4 flex items-center justify-between gap-4">
        <div className="text-sm font-medium">
          Seleccionados: {selectedIds.length} ítems
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedIds.length === 0 || selectedIds.length === items.length || loading}
            isLoading={loading}
          >
            <Split className="mr-2 h-4 w-4" />
            Crear Nueva Cuenta
          </Button>
        </div>
      </div>
    </Modal>
  );
};
